import { useState } from 'react';
import clsx from 'clsx';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MEDIA_STATE_LABELS,
  type EpisodeDTO,
  type ReleaseDTO,
  type SeasonWithEpisodes,
  type SeriesDetail as SeriesDetailType,
} from '@jelly-dl/shared';
import { api, ApiError } from '../api/client';
import { Badge, Button, Card, ErrorState, Spinner, Toggle } from '../components/ui';
import { MEDIA_STATE_COLORS } from '../lib/format';
import { ReleaseTable } from './MovieDetail';

const ACTIVE_STATES = ['searching', 'grabbed', 'downloading', 'importing'];

// Key for the release panel: which season/episode target the last search returned for.
type ReleasePanel = { key: string; releases: ReleaseDTO[] } | null;

export function SeriesDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [msg, setMsg] = useState('');
  const [panel, setPanel] = useState<ReleasePanel>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['series', id],
    queryFn: () => api.get<SeriesDetailType>(`/api/series/${id}`),
    refetchInterval: (query) =>
      (query.state.data?.seasons ?? []).some(
        (s) =>
          ACTIVE_STATES.includes(s.state) ||
          s.episodes.some((e) => ACTIVE_STATES.includes(e.state)),
      )
        ? 8_000
        : false,
  });

  const patchSeries = useMutation({
    mutationFn: (patch: { monitored?: boolean; uhd?: boolean }) => api.patch(`/api/series/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['series', id] }),
  });

  const patchSeason = useMutation({
    mutationFn: (vars: { seasonId: string; monitored: boolean }) =>
      api.patch(`/api/seasons/${vars.seasonId}`, { monitored: vars.monitored }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['series', id] }),
  });

  const patchEpisode = useMutation({
    mutationFn: (vars: { episodeId: string; monitored: boolean }) =>
      api.patch(`/api/episodes/${vars.episodeId}`, { monitored: vars.monitored }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['series', id] }),
  });

  const searchSeason = useMutation({
    mutationFn: (seasonId: string) =>
      api.post<{ releases: ReleaseDTO[] }>(`/api/seasons/${seasonId}/search`),
    onSuccess: (res, seasonId) => {
      setMsg('');
      setPanel({ key: `season:${seasonId}`, releases: res.releases });
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Search failed'),
  });

  const searchEpisode = useMutation({
    mutationFn: (episodeId: string) =>
      api.post<{ releases: ReleaseDTO[] }>(`/api/episodes/${episodeId}/search`),
    onSuccess: (res, episodeId) => {
      setMsg('');
      setPanel({ key: `episode:${episodeId}`, releases: res.releases });
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Search failed'),
  });

  const grab = useMutation({
    mutationFn: (releaseId: string) => api.post('/api/releases/grab', { releaseId }),
    onSuccess: () => {
      setMsg('');
      setPanel(null);
      qc.invalidateQueries({ queryKey: ['series', id] });
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Grab failed'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/api/series/${id}`),
    onSuccess: () => navigate('/'),
  });

  if (isLoading) return <Spinner />;
  if (isError || !data) return <ErrorState message="Couldn't load this show." onRetry={() => refetch()} />;

  const grabbingId = grab.isPending ? (grab.variables as string) : null;

  return (
    <div className="space-y-5">
      <Link to="/" className="text-xs text-slate-500 hover:text-slate-300">
        ← Library
      </Link>

      <div className="flex flex-col gap-5 sm:flex-row">
        <div className="h-72 w-48 flex-shrink-0 overflow-hidden rounded-lg bg-slate-800">
          {data.posterUrl ? (
            <img src={data.posterUrl} alt={data.title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center px-2 text-center text-xs text-slate-500">
              {data.title}
            </div>
          )}
        </div>

        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold">
                {data.title} {data.year != null && <span className="text-slate-400">({data.year})</span>}
              </h1>
              <p className="mt-1 text-sm text-slate-400">
                {data.seasonCount} seasons · {data.importedEpisodeCount}/{data.episodeCount} episodes imported
                {data.status ? ` · ${data.status}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-400">Monitored</span>
              <Toggle
                checked={data.monitored}
                onChange={(v) => patchSeries.mutate({ monitored: v })}
                label="Monitor series"
              />
              <button
                type="button"
                onClick={() => patchSeries.mutate({ uhd: !data.uhd })}
                title="Search seasons/episodes of this show in 2160p (4K) instead of 1080p."
                className={clsx(
                  'rounded-md px-2.5 py-1 text-xs font-semibold transition',
                  data.uhd
                    ? 'bg-fuchsia-600 text-white'
                    : 'border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700',
                )}
                aria-pressed={data.uhd}
              >
                4K
              </button>
              <Button
                variant="danger"
                disabled={remove.isPending}
                onClick={() => confirm('Remove this show and its episodes?') && remove.mutate()}
              >
                Remove
              </Button>
            </div>
          </div>
          {data.overview && <p className="text-sm text-slate-300">{data.overview}</p>}
        </div>
      </div>

      {msg && <p className="text-sm text-amber-400">{msg}</p>}

      <div className="space-y-3">
        {data.seasons.map((season) => (
          <SeasonBlock
            key={season.id}
            season={season}
            isOpen={open[season.id] ?? false}
            onToggleOpen={() => setOpen((o) => ({ ...o, [season.id]: !(o[season.id] ?? false) }))}
            onMonitorSeason={(v) => patchSeason.mutate({ seasonId: season.id, monitored: v })}
            onMonitorEpisode={(episodeId, v) => patchEpisode.mutate({ episodeId, monitored: v })}
            onSearchSeason={() => searchSeason.mutate(season.id)}
            onSearchEpisode={(episodeId) => searchEpisode.mutate(episodeId)}
            seasonSearchPending={searchSeason.isPending && searchSeason.variables === season.id}
            episodeSearchingId={searchEpisode.isPending ? (searchEpisode.variables as string) : null}
            panel={panel}
            grabbingId={grabbingId}
            onGrab={(releaseId) => grab.mutate(releaseId)}
          />
        ))}
        {data.seasons.length === 0 && (
          <p className="text-sm text-slate-400">No seasons found yet (metadata may still be loading).</p>
        )}
      </div>
    </div>
  );
}

function SeasonBlock({
  season,
  isOpen,
  onToggleOpen,
  onMonitorSeason,
  onMonitorEpisode,
  onSearchSeason,
  onSearchEpisode,
  seasonSearchPending,
  episodeSearchingId,
  panel,
  grabbingId,
  onGrab,
}: {
  season: SeasonWithEpisodes;
  isOpen: boolean;
  onToggleOpen: () => void;
  onMonitorSeason: (v: boolean) => void;
  onMonitorEpisode: (episodeId: string, v: boolean) => void;
  onSearchSeason: () => void;
  onSearchEpisode: (episodeId: string) => void;
  seasonSearchPending: boolean;
  episodeSearchingId: string | null;
  panel: ReleasePanel;
  grabbingId: string | null;
  onGrab: (releaseId: string) => void;
}) {
  const seasonPanelKey = `season:${season.id}`;
  return (
    <Card className="space-y-0 p-0">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <button type="button" onClick={onToggleOpen} className="flex flex-1 items-center gap-3 text-left">
          <span className="text-slate-500">{isOpen ? '▾' : '▸'}</span>
          <span className="font-medium text-slate-100">
            {season.title ?? (season.seasonNumber === 0 ? 'Specials' : `Season ${season.seasonNumber}`)}
          </span>
          <Badge className={MEDIA_STATE_COLORS[season.state]}>{MEDIA_STATE_LABELS[season.state]}</Badge>
          <span className="text-xs text-slate-500">
            {season.importedEpisodeCount}/{season.episodeCount} imported
          </span>
        </button>
        <div className="flex items-center gap-3">
          <Toggle checked={season.monitored} onChange={onMonitorSeason} label="Monitor season" />
          <Button variant="subtle" disabled={seasonSearchPending} onClick={onSearchSeason}>
            {seasonSearchPending ? 'Searching…' : 'Search season pack'}
          </Button>
        </div>
      </div>

      {panel?.key === seasonPanelKey && (
        <div className="border-t border-slate-800 px-4 py-3">
          <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">
            Season pack releases ({panel.releases.length})
          </h3>
          {panel.releases.length === 0 ? (
            <p className="text-sm text-slate-500">No candidates found.</p>
          ) : (
            <ReleaseTable releases={panel.releases} grabbingId={grabbingId} onGrab={onGrab} />
          )}
        </div>
      )}

      {isOpen && (
        <div className="border-t border-slate-800">
          <ul className="divide-y divide-slate-800/60 text-sm">
            {season.episodes.map((ep) => (
              <EpisodeRow
                key={ep.id}
                ep={ep}
                onMonitor={(v) => onMonitorEpisode(ep.id, v)}
                onSearch={() => onSearchEpisode(ep.id)}
                searching={episodeSearchingId === ep.id}
                panel={panel}
                grabbingId={grabbingId}
                onGrab={onGrab}
              />
            ))}
            {season.episodes.length === 0 && (
              <li className="px-4 py-3 text-slate-500">No episodes.</li>
            )}
          </ul>
        </div>
      )}
    </Card>
  );
}

function EpisodeRow({
  ep,
  onMonitor,
  onSearch,
  searching,
  panel,
  grabbingId,
  onGrab,
}: {
  ep: EpisodeDTO;
  onMonitor: (v: boolean) => void;
  onSearch: () => void;
  searching: boolean;
  panel: ReleasePanel;
  grabbingId: string | null;
  onGrab: (releaseId: string) => void;
}) {
  const code = `S${String(ep.seasonNumber).padStart(2, '0')}E${String(ep.episodeNumber).padStart(2, '0')}`;
  const episodePanelKey = `episode:${ep.id}`;
  return (
    <li className="px-4 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Toggle checked={ep.monitored} onChange={onMonitor} label="Monitor episode" />
          <div className="min-w-0">
            <div className="truncate text-slate-200">
              <span className="mr-2 text-xs text-slate-500">{code}</span>
              {ep.title ?? '—'}
            </div>
            <div className="text-xs text-slate-500">{ep.airDate ?? '—'}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={MEDIA_STATE_COLORS[ep.state]}>{MEDIA_STATE_LABELS[ep.state]}</Badge>
          <Button variant="subtle" disabled={searching} onClick={onSearch}>
            {searching ? 'Searching…' : 'Search'}
          </Button>
        </div>
      </div>

      {panel?.key === episodePanelKey && (
        <div className="mt-2 rounded-md border border-slate-800 p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">
            Releases ({panel.releases.length})
          </h3>
          {panel.releases.length === 0 ? (
            <p className="text-sm text-slate-500">No candidates found.</p>
          ) : (
            <ReleaseTable releases={panel.releases} grabbingId={grabbingId} onGrab={onGrab} />
          )}
        </div>
      )}
    </li>
  );
}
